import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DrawingTools } from '@shared/drawing/drawing';
import { DrawingComponent } from '@shared/drawing/drawing.component';
import { Orientation } from '@shared/viewer/enums/orientation';
import { TDocumentDefinitions, createPdf } from 'pdfmake';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged, filter, map, shareReplay, startWith,
  switchMap, tap, withLatestFrom
} from 'rxjs/operators';

import { ViewerCanvasComponent } from '../viewer-canvas/viewer-canvas.component';

import { ViewerService } from '../viewer.service';

@Component({
  selector: 'zbp-viewer-print-canvas',
  templateUrl: './viewer-print-canvas.component.html',
  styleUrls: [
    './viewer-print-canvas.component.scss',
    '../viewer-canvas/viewer-canvas.component.scss',
  ],
  imports: [CommonModule, DrawingComponent]
})
export class ViewerPrintCanvasComponent extends ViewerCanvasComponent implements OnInit, OnDestroy  {
  @Input() selectedTool: DrawingTools = DrawingTools.none;
  @Input() zoomLevel = 100;
  @Input()
  set triggerPrint(shouldPrint: boolean) {
    this.triggerPrintPdf$.next(shouldPrint);
  }
  @Output() isPrinting: EventEmitter<boolean> = new EventEmitter<boolean>();

  subscriptions: Subscription[] = [];

  imageUrl$: Observable<string> = this.background$.pipe(
    shareReplay());

  matches$: Observable<RegExp> = of(/^(?:.*\/)?([^/]+?|)(?=(?:\.[^/.]*)?$)/);

  imageLocation$: Observable<string> = this.imageUrl$.pipe(
    withLatestFrom(this.matches$, (imageUrl, matches) => ({ imageUrl, matches })),
    map(({ imageUrl, matches }) => imageUrl.replace(matches[1], '<newfilename>')),
    distinctUntilChanged()
  );

  childResourceChanged$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  updatedImageUrl$: Observable<string> = combineLatest([this.childResourceChanged$, this.imageLocation$]).pipe(
    map(([imageUrl, imageLocation]) => imageLocation.replace('<newfilename>', imageUrl))
  );

  backgroundDataUrl$: Observable<string> = this.updatedImageUrl$.pipe(
    startWith(this.getBackground()),
    switchMap(imageUrl => this.viewerService.loadImageAsBlob(imageUrl)),
  );

  canvasImage$: Observable<HTMLImageElement> = this.backgroundDataUrl$.pipe(
    switchMap(backgroundDataUrl => this.viewerService.loadImage(backgroundDataUrl)),
    shareReplay()
  );

  orientation$: Observable<Orientation> = of(this.getOrientation());
  triggerPrintPdf$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  printPdf$: Observable<void> = this.triggerPrintPdf$.pipe(
    filter(triggerPrintPdf => triggerPrintPdf),
    withLatestFrom(
      this.backgroundDataUrl$,
      this.orientation$,
      (trigger, backgroundDataUrl, orientation) => (
        { backgroundDataUrl, orientation }
      )),
    tap(({ backgroundDataUrl, orientation }) => {
      const definition: TDocumentDefinitions = {
        pageSize: 'LETTER',
        defaultStyle: {
          font: 'manuscript',
          italics: false,
          alignment: 'center',
          color: '#000000',
          decorationColor: '#000000',
          preserveLeadingSpaces: true,
        },
        pageOrientation: orientation === Orientation.Landscape ? 'landscape' : 'portrait',
        pageMargins: [0, 0, 0, 0],
        content: [],
        background: [
          {
            image: backgroundDataUrl,
            fit: orientation === Orientation.Landscape ? [11 * 72, 8.5 * 72] : [8.5 * 72, 11 * 72],
          }
        ],
      };
      createPdf(definition).open();
      this.triggerPrintPdf$.next(false);
      this.isPrinting.next(false);
    }),
    map(() => null),
    shareReplay()
  );

  constructor(viewerService: ViewerService) {
    super(viewerService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(this.printPdf$.subscribe());
    this.subscriptions.push(this.canvasImage$.subscribe());
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
